import paramChannel from '~/lib/paramChannel'
import { resolve } from '~/lib/params'
import { EFFECT_SCHEMAS } from '~/schemas/effect_schemas'
import store from '~/store'
import { setAppProperty } from '~/store/app'
import { setUIProperty } from '~/store/ui'
import { setSettingsProperty } from '~/store/settings'
import { pushHistory } from '~/store/middleware/history'
import { addLiveProp } from '~/store/playback'

export const onAppPropChange = (value, name) => {
	store.dispatch(setAppProperty([name, value]))
}

/**
 * Whether the channel can carry this key — an EFFECT PARAM, two parts.
 *
 * ⚠️ Matches `paramChannelSync.syncKey`, which mirrors nothing nested (`diffusion.advanced.*`): a transient write the mirror never reads back is a write that lands nowhere.
 */
const channelCarries = name => {
	const dot = name.indexOf('.')
	if (dot <= 0) return false
	return !!EFFECT_SCHEMAS[name.slice(0, dot)] && !name.slice(dot + 1).includes('.')
}

/**
 * A live param edit. `transient` marks a frame of a gesture still in flight, which writes the CHANNEL alone — `settleLiveProp` lands it.
 *
 * ⚠️ A fade, a scrub and a param ride are ONE shape: frequent writes to the channel, one Redux write at the end.
 */
export const onLivePropChange = (value, name, transient = false) => {
	const state = store.getState()
	// ⚠️ ANY fade in flight pins, PARKED ONES TOO: a parked child resumes where it left off rather than rebuilding, and the pin is what holds an edit across the resume.
	if (state.playback.next_preset_index >= 0) {
		store.dispatch(addLiveProp(name))
	}
	if (transient && channelCarries(name)) {
		paramChannel.set(name, value)
		return
	}
	store.dispatch({ type: 'live/setLiveProperty', payload: [name, value] })
}

/**
 * End a transient gesture, landing what the channel holds in Redux.
 *
 * ⚠️ ABSENCE IS A VALUE — a gesture ending ON the initial leaves no key, and skipping the dispatch there strands what Redux held before the drag, permanently dirty against a preset that omits it.
 *
 * ⚠️ `skipHistory`, because the gesture's history entry was pushed by `pushHistoryCallback` on mousedown. Without it a drag pushes 2-3 more entries — `RangeInput` wires this to `onPointerUp` AND `onBlur` — and the blur-time one snapshots POST-drag state, so the first Ctrl+Z does nothing visible.
 */
export const settleLiveProp = name => {
	if (!name) return
	// A key the channel never carried was written straight to Redux and is already settled; reading the channel here would land `undefined` and blank it.
	if (!channelCarries(name)) return
	const dot = name.indexOf('.')
	const effect = name.slice(0, dot)
	const key = name.slice(dot + 1)
	const prop = EFFECT_SCHEMAS[effect]?.properties?.[key]
	const resolved = resolve(paramChannel.get(name), prop)
	// Dedupe the pointerup+blur double fire. ⚠️ `?? prop?.initial` is load-bearing and does NOT violate absence-is-a-value: `setLiveProperty` prunes an at-initial write to absence, so an absent key and the initial are the SAME state. Comparing raw `undefined` here would dispatch a pointless write on every gesture that ends at the initial — while a gesture ending on the initial over a STALE Redux value still differs, and still dispatches.
	const current = store.getState().live[effect]?.[key] ?? prop?.initial
	if (resolved === current) return
	store.dispatch({
		type: 'live/setLiveProperty',
		payload: [name, resolved],
		meta: { skipHistory: true },
	})
}

export const onUiPropChange = (value, name) => {
	store.dispatch(setUIProperty([name, value]))
}

export const onSettingsPropChange = (value, name) => {
	store.dispatch(setSettingsProperty([name, value]))
}

export const onLiveBatchChange = (changes, skipHistory = false) => {
	// changes: { 'motion.pan': 50, 'motion.tilt': 60 }
	const payload = Object.entries(changes)
	store.dispatch({ type: 'live/setLiveProperties', payload, meta: { skipHistory } })
}

export const pushHistoryCallback = () => {
	store.dispatch(pushHistory)
}
